import { BaseComponent } from '@shared/components/base.component'
import * as Leaflet from 'leaflet'
import { GestureHandling } from 'leaflet-gesture-handling'
import { PinsService } from '@shared/services/pins.service'
import { GreencoinActivityService } from '@shared/services/greencoin-activity.service'
import { ComponentRef, Directive, ViewContainerRef } from '@angular/core'
import { OpenStreetMapService } from '@shared/services/open-street-map.service'
import { MapPin, ProjectPin } from '@shared/interfaces/pins.interface'
import { MapInfoWindowAmbassadorComponent } from '@layouts/default/components/map-infowindow-ambassador/map-infowindow-ambassador.component'
import { MapInfoWindowEventComponent } from '@layouts/default/components/map-infowindow-event/map-infowindow-event.component'
import { MapInfoWindowProjectComponent } from '@layouts/default/components/map-infowindow-project/map-infowindow-project.component'
import { GreencoinActivity } from '@shared/interfaces/greencoin-activity.interface'

Leaflet.Map.addInitHook('addHandler', 'gestureHandling', GestureHandling);

@Directive()
export default class OpenStreetMapBaseComponent extends BaseComponent {
    public map: Leaflet.Map = null;
    public markersCluster: Leaflet.MarkerClusterGroup = null;
    public activeMarker: Leaflet.Marker = null;
    public activeMarkerPin: MapPin = null;

    public MARKER_ICON_WIDTH: number = 30
    public MARKER_ICON_HEIGHT: number = 40

    public POPUP_X_OFFSET: number = -205
    public POPUP_Y_OFFSET: number = 275

    constructor(
        public pinsService: PinsService,
        public greencoinActivityService: GreencoinActivityService,
        public viewContainerRef: ViewContainerRef,
        public openStreetMapService: OpenStreetMapService
    ) {
        super();

        this.translate.onLangChange.subscribe(() => {
            this.updateActiveMarkerPopupContent()
        })
    }

    onMapLoaded(map: Leaflet.Map) {
        this.map = map;
        this.afterMapLoad();
    }

    afterMapLoad() {
    }

    onMarkerClusterLoaded(markersCluster: Leaflet.MarkerClusterGroup) {
        this.markersCluster = markersCluster;
        this.afterClusterLoad();
    }

    markersClusterOptions(): Leaflet.MarkerClusterGroupOptions {
        return {
            showCoverageOnHover: false
        } as Leaflet.MarkerClusterGroupOptions;
    }

    afterClusterLoad() {
    }

    removeActiveMarker(): void {
        if (this.activeMarker == null) {
            return;
        }

        this.activeMarker?.closePopup();
        this.activeMarker?.unbindPopup()

        this.activeMarker = null;
        this.activeMarkerPin = null;
    }

    getComponentForPopupContent(pin: MapPin): ComponentRef<any> {
        this.viewContainerRef.clear();
        if (pin.markerType == 'ambassador') {
            return this.viewContainerRef.createComponent(MapInfoWindowAmbassadorComponent);
        } else if (pin.markerType.endsWith('event')) {
            return this.viewContainerRef.createComponent(MapInfoWindowEventComponent);
        } else {
            return this.viewContainerRef.createComponent(MapInfoWindowProjectComponent);
        }
    }

    popupWindowContent(pin: MapPin): string|null {
        const infoWindowComponent = this.getComponentForPopupContent(pin);
        infoWindowComponent.instance.pin = pin;
        infoWindowComponent.changeDetectorRef.detectChanges();

        const result: string = infoWindowComponent.location.nativeElement.innerHTML;

        this.viewContainerRef.clear();
        return result;
    }

    addMarkerEvents(marker: Leaflet.Marker, pin: MapPin): void {
        const that = this
        marker.addEventListener('click', function () {
            that.removeActiveMarker()

            that.activeMarker = marker
            that.activeMarkerPin = pin

            const popupContent = that.popupWindowContent(pin)
            if (popupContent != null) {
                marker.bindPopup(popupContent, {
                    offset: [that.POPUP_X_OFFSET, that.POPUP_Y_OFFSET]
                })

                marker.getPopup().on('remove', function () {
                    that.removeActiveMarker()
                })
                marker.openPopup()
            }

            that.map.panTo(marker.getLatLng())

            that.loadProjectGreencoinActivity(pin as ProjectPin)
        })
    }

    updateActiveMarkerPopupContent(): void {
        if (this.activeMarker == null) {
            return;
        }

        const popupContent = this.popupWindowContent(this.activeMarkerPin)
        if (popupContent != null) {
            this.activeMarker.setPopupContent(popupContent)
        }
    }

    loadProjectGreencoinActivity(projectPin: ProjectPin): void {
        if (projectPin.greencoin_activity != null) {
            return;
        }

        const that = this;

        this.greencoinActivityService.getProjectActivity(projectPin.id).subscribe({
            next: function (response: GreencoinActivity) {
                projectPin.greencoin_activity = response;
                that.updateActiveMarkerPopupContent()
            }
        });
    }
}
